"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import {
  type DotMark,
  DotPlotChart,
} from "@/components/tool-kit/charts/dot-plot-chart";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { mean, median } from "@/lib/stats/descriptive";
import {
  type DotStack,
  dotPlotAxis,
  dotStacks,
} from "@/lib/tool-kit/charts/dot-plot";
import {
  type NumberFormat,
  usesDecimalComma,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  commonDenominator,
  formatFraction,
  parseFractionList,
} from "@/lib/tool-kit/charts/fractions";
import { chartFilename } from "@/lib/tool-kit/charts/layout";

/** English strings. A translated content file overrides these through `ui`. */
export const DOT_PLOT_MAKER_STRINGS = {
  dataLabel: "Your data — whole numbers, decimals or fractions",
  dataHint:
    "Separate values with commas, spaces or new lines. Fractions and mixed numbers work too — 3/4, 1 1/2 — but then separate values with commas or new lines, since a space joins a mixed number. Use semicolons if your numbers have decimal commas.",
  exampleData: "2, 3, 3, 4, 4, 4, 5, 5, 5, 5, 6, 6, 7, 9",
  exampleTitle: "Books Read Over the Summer",
  exampleXTitle: "Number of books",
  binLabel: "Group into bins of width (optional)",
  binPlaceholder: "One stack per value",
  markLabel: "Marks",
  dots: "Dots",
  xMarks: "X marks",
  summaryHeading: "Counts",
  valueColumn: "Value",
  binColumn: "Bin",
  countColumn: "Count",
  statN: "Values (n)",
  statMean: "Mean",
  statMedian: "Median",
  statMode: "Mode",
  statRange: "Range",
  noMode: "None — every value appears once",
  valuesDetected: "one:{count} value|other:{count} values",
  binNote:
    "Each bin includes its lower edge and excludes its upper edge, [a, b), except the last, which includes both. Its dots are stacked over the bin's middle.",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Number-line title",
  axisTitlePlaceholderX: "Value",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the count above each stack",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy counts",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  chartLabel: "Dot plot",
  chartDescription:
    "Dot plot of {values} from {min} to {max}. The tallest stack is at {mode}, with {count} dots.",
  enterData: "Enter at least one number to draw the dot plot.",
  badBin:
    "A bin width must be greater than 0, so each value has its own stack.",
  tooSpread:
    "These values are spread too thinly for one stack per value. Try a bin width to group them.",
  invalidTokens:
    "one:Left out {count} entry that is not a number: {tokens}.|other:Left out {count} entries that are not numbers: {tokens}.",
} as const;

type Strings = typeof DOT_PLOT_MAKER_STRINGS;

/** A value as the reader wrote it: a fraction where the data used fractions. */
function showValue(
  value: number,
  denominator: number | null,
  fmt: NumberFormat
): string {
  return (
    (denominator ? formatFraction(value, denominator) : null) ??
    valueText(fmt, value, 4)
  );
}

/** The modes, or null when every value appears exactly once. */
function modes(stacks: DotStack[]): number[] | null {
  const top = Math.max(0, ...stacks.map((stack) => stack.count));
  if (top <= 1 && stacks.length > 1) {
    return null;
  }
  return stacks
    .filter((stack) => stack.count === top)
    .map((stack) => stack.position);
}

function Summary({
  values,
  denominator,
  strings,
  fmt,
}: {
  values: number[];
  denominator: number | null;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const separator = usesDecimalComma(fmt) ? "; " : ", ";
  const show = (value: number) => showValue(value, denominator, fmt);
  // The mode is a property of the values, not of the bins they were grouped into.
  const found = modes(dotStacks(values));
  return (
    <StatGrid>
      <StatCard label={strings.statN} value={fmt(values.length)} />
      <StatCard
        label={strings.statMean}
        value={valueText(fmt, mean(values), 4)}
      />
      <StatCard label={strings.statMedian} value={show(median(values))} />
      <StatCard
        label={strings.statMode}
        value={found ? found.map(show).join(separator) : strings.noMode}
      />
      <StatCard
        label={strings.statRange}
        value={show(Math.max(...values) - Math.min(...values))}
      />
    </StatGrid>
  );
}

export default function DotPlotMakerWidget() {
  const strings = useToolUi(DOT_PLOT_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [bin, setBin] = useState("");
  const [mark, setMark] = useState<DotMark>("dot");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parsed = parseFractionList(raw);
  const { values } = parsed;
  const binWidth = parseNumberInput(bin);
  const badBin = bin.trim() !== "" && !(binWidth !== null && binWidth > 0);
  const width = badBin ? null : binWidth;
  const stacks = dotStacks(values, width);
  const denominator =
    parsed.denominators.length > 0
      ? commonDenominator(parsed.denominators)
      : null;
  const axis = dotPlotAxis(stacks, { denominator, binWidth: width });
  const crowded = !width && stacks.length > 40;
  const binned = width !== null;
  const show = (value: number) => showValue(value, denominator, fmt);
  const separator = usesDecimalComma(fmt) ? "; " : ", ";
  const head = [
    binned ? strings.binColumn : strings.valueColumn,
    strings.countColumn,
  ];
  const rows = stacks.map((stack, index) => {
    // Bins are [a, b) except the last, which is closed: [a, b].
    const close = index === stacks.length - 1 ? "]" : ")";
    const label = binned
      ? `[${show(stack.start ?? 0)}${separator}${show(stack.end ?? 0)}${close}`
      : show(stack.position);
    return [label, fmt(stack.count)];
  });
  const tallest = stacks.reduce(
    (best, stack) => (stack.count > best.count ? stack : best),
    stacks[0]
  );

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="dpm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={5}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {values.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.valuesDetected, locale, values.length, {
              count: fmt(values.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {parsed.invalid.length > 0 ? (
          <Notice>
            {tPlural(strings.invalidTokens, locale, parsed.invalid.length, {
              count: fmt(parsed.invalid.length),
              tokens: parsed.invalid.slice(0, 5).join(" "),
            })}
          </Notice>
        ) : null}
        {badBin ? <ErrorNotice>{strings.badBin}</ErrorNotice> : null}
        {crowded ? <Notice>{strings.tooSpread}</Notice> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={binned ? strings.binNote : undefined}
          id="dpm-bin"
          label={strings.binLabel}
          numeric
          onChange={setBin}
          placeholder={strings.binPlaceholder}
          value={bin}
        />
        <PillGroup
          label={strings.markLabel}
          name="dpm-mark"
          onChange={setMark}
          options={[
            { value: "dot", label: strings.dots },
            { value: "x", label: strings.xMarks },
          ]}
          value={mark}
        />
      </div>
      {values.length > 0 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "dot-plot")}
          labels={strings}
          note={style.note}
          table={null}
          title={style.title}
        >
          <DotPlotChart
            axis={axis}
            description={t(strings.chartDescription, {
              values: tPlural(strings.valuesDetected, locale, values.length, {
                count: fmt(values.length),
              }),
              min: show(Math.min(...values)),
              max: show(Math.max(...values)),
              mode: show(tallest.position),
              count: fmt(tallest.count),
            })}
            fmt={fmt}
            id="dpm-chart"
            label={style.title.trim() || strings.chartLabel}
            mark={mark}
            stacks={stacks}
            style={style}
          />
        </ChartFigure>
      ) : null}
      {values.length > 0 ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.summaryHeading}</h2>
          <Summary
            denominator={denominator}
            fmt={fmt}
            strings={strings}
            values={values}
          />
          <DataTable head={head} rows={rows} />
        </section>
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="dpm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      />
    </div>
  );
}
